import React from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  UsersIcon,
  CreditCardIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useGetCompanyDetailsQuery } from '../../../store/api/superAdminApi';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import Modal from '../../../components/common/Modal';

const CompanyDetailsModal = ({ company, isOpen, onClose }) => {
  const { data, isLoading, error } = useGetCompanyDetailsQuery(company?._id, {
    skip: !company?._id || !isOpen,
  });

  const companyDetails = data?.data?.company || company;
  const subscription = data?.data?.subscription;
  const stats = data?.data?.stats || {};
  const admins = data?.data?.admins || [];

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Company Details" size="lg">
        <div className="p-6">
          <LoadingSpinner />
        </div>
      </Modal>
    );
  }

  if (!companyDetails) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center mr-4">
              <BuildingOfficeIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{companyDetails.name}</h2>
              <p className="text-gray-600">{companyDetails.legalName}</p>
              <p className="text-sm text-gray-500">Code: {companyDetails.code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 rounded-lg p-4 border border-blue-200"
          >
            <div className="flex items-center">
              <UsersIcon className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-blue-600">Total Employees</p>
                <p className="text-xl font-bold text-blue-900">{stats.totalEmployees || 0}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-green-50 rounded-lg p-4 border border-green-200"
          >
            <div className="flex items-center">
              <UsersIcon className="w-6 h-6 text-green-600 mr-3" />
              <div>
                <p className="text-sm text-green-600">Active Employees</p>
                <p className="text-xl font-bold text-green-900">{stats.activeEmployees || 0}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-purple-50 rounded-lg p-4 border border-purple-200"
          >
            <div className="flex items-center">
              <UsersIcon className="w-6 h-6 text-purple-600 mr-3" />
              <div>
                <p className="text-sm text-purple-600">Admins</p>
                <p className="text-xl font-bold text-purple-900">{stats.admins || 0}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Company Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPinIcon className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Address</p>
                  <p className="text-gray-600">
                    {companyDetails.address?.street && (
                      <>{companyDetails.address.street}<br /></>
                    )}
                    {companyDetails.address?.city && companyDetails.address.city}
                    {companyDetails.address?.state && `, ${companyDetails.address.state}`}
                    {companyDetails.address?.country && `, ${companyDetails.address.country}`}
                    {companyDetails.address?.zipCode && ` - ${companyDetails.address.zipCode}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-gray-600">{companyDetails.email || 'Not provided'}</p>
                </div>
              </div>

              <div className="flex items-center">
                <PhoneIcon className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Phone</p>
                  <p className="text-gray-600">{companyDetails.phone || 'Not provided'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700">Country & Currency</p>
                <p className="text-gray-600">
                  {companyDetails.countryCode} • {companyDetails.currency}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700">Timezone</p>
                <p className="text-gray-600">{companyDetails.timezone}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  companyDetails.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {companyDetails.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Subscription Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h3>
            {subscription ? (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center">
                  <CreditCardIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Plan</p>
                    <p className="text-gray-900 font-semibold">{subscription.planId?.name}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Pricing</p>
                  <p className="text-gray-900">
                    {formatCurrency(subscription.price, companyDetails.currency)} / {subscription.billingCycle}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    subscription.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : subscription.status === 'trial'
                      ? 'bg-blue-100 text-blue-800'
                      : subscription.status === 'past_due'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {subscription.status}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Start Date</p>
                  <p className="text-gray-600">{formatDate(subscription.startDate)}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">End Date</p>
                  <p className="text-gray-600">{formatDate(subscription.endDate)}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Limits</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {subscription.limits?.maxEmployees || 'Unlimited'} employees</li>
                    <li>• {subscription.limits?.maxBranches || 'Unlimited'} branches</li>
                    <li>• {subscription.limits?.storage || 'Unlimited'} GB storage</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-gray-500">No active subscription</p>
              </div>
            )}
          </div>
        </div>

        {/* Admin Users */}
        {admins.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Users</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div key={admin._id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {admin.firstName} {admin.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{admin.email}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        admin.role === 'company_admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {admin.role.replace('_', ' ')}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        Last login: {admin.lastLogin ? formatDate(admin.lastLogin) : 'Never'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CompanyDetailsModal;